import React, { createContext, useContext, useState, ReactNode, useEffect, useRef } from 'react';
import { useAuth } from './AuthContext';
import logger from '../utils/logger';

interface UserFilterContextType {
  selectedUserId: string | null;
  setSelectedUserId: (userId: string | null) => void;
}

const UserFilterContext = createContext<UserFilterContextType | undefined>(undefined);

// Clé utilisée pour stocker la sélection d'utilisateur dans le localStorage
const STORAGE_KEY = 'immo-prospect-selected-user-id';

export const UserFilterProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { supabaseUser } = useAuth();
  
  // Référence pour tracker l'ID de l'utilisateur précédent
  const previousUserIdRef = useRef<string | null>(null);
  
  // Initialiser avec l'ID de l'utilisateur connecté (sera mis à jour par useEffect)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(supabaseUser?.id || null);

  // Fonction pour mettre à jour la sélection d'utilisateur et la persister
  const handleSetSelectedUserId = (userId: string | null) => {
    try {
      // Stocker la nouvelle valeur dans le localStorage
      localStorage.setItem(STORAGE_KEY, userId === null ? 'null' : userId);
      logger.info(`Filtre utilisateur mis à jour et persisté: ${userId === null ? 'Tous les utilisateurs' : userId}`);
    } catch (error) {
      logger.error('Erreur lors de la persistance du filtre utilisateur:', error);
    }
    
    // Mettre à jour l'état
    setSelectedUserId(userId);
  };
  
  // Mettre à jour le filtre d'utilisateur lorsque l'utilisateur connecté change
  useEffect(() => {
    if (supabaseUser?.id) {
      const currentUserId = supabaseUser.id;
      
      // Détecter un changement d'utilisateur (nouvelle connexion après déconnexion)
      // Si l'utilisateur précédent était null (déconnecté) et maintenant il y a un utilisateur
      // OU si l'ID de l'utilisateur a changé
      if (previousUserIdRef.current !== currentUserId) {
        logger.info(`Changement d'utilisateur détecté - Initialisation du filtre à: ${currentUserId}`);
        setSelectedUserId(currentUserId);
        localStorage.setItem(STORAGE_KEY, currentUserId);
        previousUserIdRef.current = currentUserId;
      }
    } else {
      // L'utilisateur s'est déconnecté
      if (previousUserIdRef.current !== null) {
        logger.info('Déconnexion détectée - Nettoyage du filtre utilisateur');
        setSelectedUserId(null);
        localStorage.removeItem(STORAGE_KEY);
        previousUserIdRef.current = null;
      }
    }
  }, [supabaseUser?.id]);

  return (
    <UserFilterContext.Provider value={{ selectedUserId, setSelectedUserId: handleSetSelectedUserId }}>
      {children}
    </UserFilterContext.Provider>
  );
};

export const useUserFilter = (): UserFilterContextType => {
  const context = useContext(UserFilterContext);
  if (context === undefined) {
    throw new Error('useUserFilter must be used within a UserFilterProvider');
  }
  return context;
};

